import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isCancelled, publishingButton, publishingInput, useMenuRequest } from './menuPublishingApi';

const phases = { pending_dns: 'Pending DNS', pending_certificate: 'Pending certificate', active: 'Active', failed: 'Failed', disabled: 'Disabled' };

export default function MenuDomains({ base, published }) {
  const request = useMenuRequest();
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const [domains, setDomains] = useState([]);
  const [hostname, setHostname] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await request(`${base}/domains`);
      setDomains(result.domains || []);
      setLoading(false);
    } catch (failure) {
      if (!isCancelled(failure)) { setError(failure.message); setLoading(false); }
    }
  }, [base, request]);
  useEffect(() => { load(); }, [load]);

  async function operate(operation, domain) {
    setError(''); setBusy(true); setConfirmation(null);
    const create = operation === 'create';
    const path = create ? `${base}/domains` : `${base}/domains/${domain.id}${operation === 'remove' ? '' : `/${operation}`}`;
    try {
      const result = await request(path, { method: operation === 'remove' ? 'DELETE' : 'POST',
        ...(create ? { body: JSON.stringify({ hostname: hostname.trim() }) } : {}) });
      setDomains((current) => operation === 'remove' ? current.filter((entry) => entry.id !== domain.id)
        : [...current.filter((entry) => entry.id !== result.id), result].sort((a, b) => a.id - b.id));
      if (create) setHostname('');
      setBusy(false);
    } catch (failure) {
      if (!isCancelled(failure)) {
        setBusy(false);
        // Failed creation may have reserved a claim; reload it before offering a retry.
        await load();
        if (alive.current) setError(failure.message);
      }
    }
  }

  return <section className="mt-6 border-t border-gray-700 pt-4" aria-label="Customer menu domains">
    <h3 className="text-lg font-semibold">Custom menu domain</h3>
    <p className="text-sm text-gray-300 my-2">Connect a hostname such as menu.yourvenue.com. Your existing website stays with its provider. Keeping its /menu page will use an embed in a later step; DNS connects hostnames, not page paths.</p>
    {!published && <p className="text-amber-200 text-sm">You can prepare a domain while the menu is a draft. It serves guests only while the menu is published and the domain is active.</p>}
    {error && <p role="alert" className="text-red-300 my-2">{error}</p>}
    {loading ? <p role="status">Loading domains…</p> : <>
      <button type="button" className={publishingButton} disabled={busy} onClick={load}>Refresh domain status</button>
      <p className="text-xs text-gray-400 mt-1">Refresh reads saved status. Check DNS and certificate contacts the provider and verifies DNS again.</p>
      {domains.map((domain) => <article key={domain.id} className="rounded border border-gray-600 p-4 my-3">
        <h4 className="font-semibold break-all">{domain.hostname}</h4>
        <p className="my-2">Status: {phases[domain.phase] || 'Pending verification'}</p>
        {domain.phase === 'active' && !published && <p className="text-amber-200 text-sm">Domain verified; menu is unpublished.</p>}
        <p className="text-sm">In your DNS provider, add a CNAME for <strong className="break-all">{domain.hostname}</strong> pointing to <strong className="break-all">{domain.expectedCname}</strong>.</p>
        <p className="text-xs text-gray-400 mt-1">Use a visible CNAME subdomain. Flattened/proxied records and root domains need separate validation. DNS and certificates can take time; check again after records propagate.</p>
        <dl className="text-sm my-2 break-words">
          <dt>Provider hostname / TLS</dt><dd>{domain.cloudflareHostnameStatus || 'Pending'} / {domain.cloudflareSslStatus || 'Pending'}</dd>
          {domain.cloudflareOwnershipName && <><dt>Ownership verification name</dt><dd className="break-all">{domain.cloudflareOwnershipName}</dd><dt>Ownership verification value</dt><dd className="break-all">{domain.cloudflareOwnershipValue}</dd></>}
          {domain.cloudflareSslValidationName && <><dt>Certificate verification name</dt><dd className="break-all">{domain.cloudflareSslValidationName}</dd><dt>Certificate verification value</dt><dd className="break-all">{domain.cloudflareSslValidationValue}</dd></>}
          {domain.lastCheckedAt && <><dt>Last checked</dt><dd>{String(domain.lastCheckedAt)}</dd></>}
        </dl>
        {domain.failureReason && <p role="alert" className="text-red-300 text-sm break-words">{domain.failureReason}</p>}
        <div className="flex flex-wrap gap-2">
          <button type="button" className={publishingButton} disabled={busy} onClick={() => operate('check', domain)}>{domain.phase === 'disabled' ? 'Re-enable and check' : 'Check DNS and certificate'}</button>
          <button type="button" className={publishingButton} disabled={busy || domain.phase === 'disabled'} onClick={() => setConfirmation({ operation: 'disable', domain })}>Disable domain</button>
          <button type="button" className={publishingButton} disabled={busy} onClick={() => setConfirmation({ operation: 'remove', domain })}>Remove domain</button>
          {domain.phase === 'active' && published && <a className="underline p-2" href={`https://${domain.hostname}/`} target="_blank" rel="noopener noreferrer">Open menu on domain</a>}
        </div>
      </article>)}
      {!domains.length && <p className="text-sm text-gray-400 my-3">No menu domains connected. The platform menu address remains available.</p>}
    </>}
    {confirmation && <div role="alert" className="rounded border border-amber-500 p-3 my-3">
      <p>{confirmation.operation === 'remove' ? 'Remove' : 'Disable'} {confirmation.domain.hostname}? Its menu address will stop working. Platform and printed QR addresses stay available. {confirmation.operation === 'remove' ? 'Removal deletes its provider association and releases the hostname.' : 'Disabling retains the hostname for re-enabling.'}</p>
      <div className="flex gap-2 mt-2"><button type="button" className={publishingButton} disabled={busy} onClick={() => operate(confirmation.operation, confirmation.domain)}>Confirm {confirmation.operation}</button>
        <button type="button" className={publishingButton} onClick={() => setConfirmation(null)}>Cancel</button></div>
    </div>}
    <form className="mt-4 flex flex-wrap items-end gap-2" onSubmit={(event) => { event.preventDefault(); operate('create'); }}>
      <label className="flex-1">Menu hostname<input className={publishingInput} value={hostname} onChange={(event) => setHostname(event.target.value)} placeholder="menu.yourvenue.com" required maxLength={255} disabled={busy || loading} /></label>
      <button className={publishingButton} disabled={busy || loading || !hostname.trim()}>Connect domain</button>
    </form>
    <p className="text-xs text-gray-400 mt-2">Connecting or checking a domain requires domain provisioning to be enabled by your Suitestream administrator.</p>
    {busy && <p role="status" className="mt-2">Updating domain…</p>}
  </section>;
}
